import * as calendar from '../actions/calendar';
import * as user from '../actions/user';
import * as engage from '../actions/engage';
import * as engagement from '../actions/engagement';
import { CalendarEventDetails } from '../models/calendar/calendar-event-details.model';
import { CalendarConstants } from '../calendar/calendar.constants';
import { equals } from 'ramda';
import { DiscoverConstants } from '../discover/discover.constants';
import moment from 'moment-timezone';

export interface State {
  calendarEventDetails: CalendarEventDetails;
  campaignToggleActive: boolean;
  communityCareToggleActive: boolean;
  currentDate: any;
  currentView: string;
  earliestShownDate?: any;
  emailToggleActive: boolean;
  error?: any;
  eventToggleActive: boolean;
  freeItemInCartPromotionActive: boolean;
  incentivizeOrderToggleActive: boolean;
  latestShownDate?: any;
  nationalEmailToggleActive: boolean;
  nationalThirdPartyPromotionToggleActive: boolean;
  previewHtml: string;
  rewardAutomationToggleActive: boolean;
  rewardOneRecipientToggleActive: boolean;
  rewardToggleActive: boolean;
  searchObj: any;
  thirdPartyPromotionToggleActive: boolean;
}

export const initialState: State = {
  calendarEventDetails: null,
  campaignToggleActive: false,
  communityCareToggleActive: true,
  currentDate: moment(),
  currentView: 'Month',
  emailToggleActive: true,
  eventToggleActive: true,
  freeItemInCartPromotionActive: true,
  incentivizeOrderToggleActive: true,
  nationalEmailToggleActive: true,
  nationalThirdPartyPromotionToggleActive: true,
  previewHtml: '',
  rewardAutomationToggleActive: true,
  rewardOneRecipientToggleActive: false,
  rewardToggleActive: true,
  searchObj: {
    searchTerm: '',
    searchBy: CalendarConstants.searchByCategories[0].value
  },
  thirdPartyPromotionToggleActive: true
};

export function reducer(state = initialState, action: calendar.Actions | user.Actions | engage.Actions | engagement.Actions): State {
  switch (action.type) {
    case calendar.ActionTypes.CALENDAR_CLEAR_SEARCH: {
      let searchTerm = action.payload;
      const searchObj = {
        searchTerm,
        searchBy: state.searchObj.searchBy
      };
      return {
        ...state,
        searchObj,
      };
    }

    case calendar.ActionTypes.CALENDAR_DATES_CHANGED: {
      let { oldValue, newValue, view, currDate } = action.payload;
      let currentDate = equals(newValue, oldValue) ? state.currentDate : currDate;
      let currentView = equals(newValue, oldValue) ? state.currentView : view;
      let earliestShownDate = equals(newValue, oldValue) && state.earliestShownDate
        ? state.earliestShownDate : newValue.startDate;
      let latestShownDate = equals(newValue, oldValue) && state.latestShownDate
        ? state.latestShownDate : newValue.endDate;

      return {
        ...state,
        earliestShownDate,
        latestShownDate,
        currentView,
        currentDate,
      };
    }

    case calendar.ActionTypes.CALENDAR_SEARCH_CRITERIA_CHANGED: {
      const searchObj = {
        searchTerm: '',
        searchBy: action.payload
      };
      return {
        ...state,
        searchObj,
      };
    }

    case calendar.ActionTypes.CALENDAR_SEARCH_TERM_CHANGED: {
      const searchObj = {
        searchTerm: action.payload,
        searchBy: state.searchObj.searchBy
      };
      return {
        ...state,
        searchObj,
      };
    }

    case calendar.ActionTypes.CALENDAR_SWITCH_LOCATIONS: {
      const searchObj = {
        searchTerm: null,
        searchBy: state.searchObj.searchBy
      };
      return {
        ...state,
        searchObj,
      };
    }

    case calendar.ActionTypes.CALENDAR_TOGGLED: {
      let calendar = action.payload;
      let rewardToggleActive = state.rewardToggleActive;
      let rewardAutomationToggleActive = state.rewardAutomationToggleActive;
      let rewardOneRecipientToggleActive = state.rewardOneRecipientToggleActive;
      let eventToggleActive = state.eventToggleActive;
      let emailToggleActive = state.emailToggleActive;
      let nationalEmailToggleActive = state.nationalEmailToggleActive;
      let incentivizeOrderToggleActive = state.incentivizeOrderToggleActive;
      let communityCareToggleActive = state.communityCareToggleActive;
      let thirdPartyPromotionToggleActive = state.thirdPartyPromotionToggleActive;
      let nationalThirdPartyPromotionToggleActive = state.nationalThirdPartyPromotionToggleActive;
      let freeItemInCartPromotionActive = state.freeItemInCartPromotionActive;
      if (calendar.calendarIds.includes(CalendarConstants.offer)) {
        rewardToggleActive = calendar.active;
        calendar.subToggles.map(subToggle => {
          if (subToggle.categoryId === CalendarConstants.automationId) {
            rewardAutomationToggleActive = subToggle.active;
          } else if (subToggle.categoryId === CalendarConstants.singleRecipientId) {
            rewardOneRecipientToggleActive = subToggle.active;
          }
        });
      } else if (calendar.calendarIds.includes(CalendarConstants.event)) {
        eventToggleActive = calendar.active;
      } else if (calendar.calendarIds.includes(CalendarConstants.email)) {
        emailToggleActive = calendar.active;
      } else if (calendar.calendarIds.includes(CalendarConstants.nationalEmail)) {
        nationalEmailToggleActive = calendar.active;
      } else if (calendar.calendarIds.includes(CalendarConstants.bonusPoint)) {
        incentivizeOrderToggleActive = calendar.active;
      } else if (calendar.calendarIds.includes(CalendarConstants.communityCareEvent)) {
        communityCareToggleActive = calendar.active;
      } else if (calendar.calendarIds.includes(CalendarConstants.thirdPartyPromotion)) {
        thirdPartyPromotionToggleActive = calendar.active;
      } else if (calendar.calendarIds.includes(CalendarConstants.nationalThirdPartyPromotion)) {
        nationalThirdPartyPromotionToggleActive = calendar.active;
      } else if (calendar.calendarIds.includes(DiscoverConstants.freeItemInCartPromotion)) {
        freeItemInCartPromotionActive = calendar.active;
      }

      return {
        ...state,
        rewardToggleActive,
        rewardAutomationToggleActive,
        rewardOneRecipientToggleActive,
        eventToggleActive,
        emailToggleActive,
        nationalEmailToggleActive,
        incentivizeOrderToggleActive,
        communityCareToggleActive,
        thirdPartyPromotionToggleActive,
        nationalThirdPartyPromotionToggleActive,
        freeItemInCartPromotionActive
      };
    }

    case calendar.ActionTypes.CAMPAIGNS_TOGGLED: {
      return { ...state, campaignToggleActive: !state.campaignToggleActive };
    }

    case calendar.ActionTypes.FETCH_AUTOMATED_REWARD_DETAILS_SUCCESS:
    case calendar.ActionTypes.FETCH_REWARD_DETAILS_SUCCESS: {
      const calendarEventDetails = action.payload;
      return { ...state, calendarEventDetails };
    }

    case calendar.ActionTypes.FETCH_CALENDAR_EVENTS_FAILURE: {
      const error = action.payload;
      return { ...state, error };
    }

    default: {
      return state;
    }
  }
}

export const getCalendarEventDetails = (state: State) => state.calendarEventDetails;

export const getCampaignToggleActive = (state: State) => state.campaignToggleActive;

export const getCommunityCareToggleActive = (state: State) => state.communityCareToggleActive;

export const getCurrentDate = (state: State) => state.currentDate;

export const getCurrentView = (state: State) => state.currentView;

export const getEarliestShownDate = (state: State) => state.earliestShownDate;

export const getEmailToggleActive = (state: State) => state.emailToggleActive;

export const getError = (state: State) => state.error;

export const getEventToggleActive = (state: State) => state.eventToggleActive;

export const getFreeItemInCartPromotionToggleActive = (state: State) => state.freeItemInCartPromotionActive;

export const getIncentivizeOrderToggleActive = (state: State) => state.incentivizeOrderToggleActive;

export const getLatestShownDate = (state: State) => state.latestShownDate;

export const getNationalEmailToggleActive = (state: State) => state.nationalEmailToggleActive;

export const getNationalThirdPartyPromotionToggleActive = (state: State) => state.nationalThirdPartyPromotionToggleActive;

export const getPreviewHtml = (state: State) => state.previewHtml;

export const getRewardAutomationToggleActive = (state: State) => state.rewardAutomationToggleActive;

export const getRewardOneRecipientToggleActive = (state: State) => state.rewardOneRecipientToggleActive;

export const getRewardToggleActive = (state: State) => state.rewardToggleActive;

export const getSearchObj = (state: State) => state.searchObj;

export const getThirdPartyPromotionToggleActive = (state: State) => state.thirdPartyPromotionToggleActive;
